import React, { useState } from 'react';
import { AutoComplete, TaskTypeIcon } from '@spark';
import {
    expandTaskTypeFilterValues,
    TASK_TYPE_FILTER_OPTIONS,
} from '@/features/tasks/utils/taskTypeFilter';

/**
 * Reusable Task Type Filter (Spark AutoComplete — same component / scroll
 * behavior as Filter By User and Filter By Status).
 *
 * Contract: value is `allValue` ("All") or an array of filter values
 * (`ClassificationResponse:single` / `:multi`, or a TaskType). onChange
 * receives that array, or `allValue` when the selection is cleared.
 *
 * `agentic` (the Projects "Agentic" toggle) switches the option tiles to
 * their robot-badged variant.
 */
const TaskTypeFilter = ({
    value,
    onChange,
    agentic = false,
    placeholder = 'All Task Types!',
    allValue = 'All',
    className = 'task-type-filter-select',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const selectedValues = React.useMemo(
        () => (value && value !== allValue ? expandTaskTypeFilterValues(value) : []),
        [value, allValue],
    );

    const toggle = (taskType) => {
        const next = selectedValues.includes(taskType)
            ? selectedValues.filter((v) => v !== taskType)
            : [...selectedValues, taskType];
        onChange(next.length === 0 ? allValue : next);
    };

    // Kept aligned with selectedValues (no filtering) so tag index maps back.
    const tags = selectedValues.map(
        (taskType) => TASK_TYPE_FILTER_OPTIONS.find((o) => o.value === taskType)?.label || String(taskType),
    );

    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions = normalizedQuery
        ? TASK_TYPE_FILTER_OPTIONS.filter((o) => o.label.toLowerCase().includes(normalizedQuery))
        : TASK_TYPE_FILTER_OPTIONS;

    const items = filteredOptions.map((option) => ({
        label: option.label,
        prefix: (
            <TaskTypeIcon
                type={option.iconType}
                size={24}
                agentic={agentic}
                showTooltip={false}
            />
        ),
        checked: selectedValues.includes(option.value),
        onClick: () => toggle(option.value),
    }));

    return (
        <div
            className={className}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
        >
            <AutoComplete
                type="multi-select"
                size="medium"
                placeholder={placeholder}
                open={open}
                showChevron
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery('');
                }}
                value={query}
                tags={tags}
                items={items}
                onChange={(e) => setQuery(e.target.value)}
                onTagRemove={(_tag, index) => toggle(selectedValues[index])}
            />
        </div>
    );
};

export default TaskTypeFilter;
